import React from 'react'
import { Alert, Select } from '@sainsburys-tech/bolt'
import { useBrandConfigContext } from '../../providers/BrandConfigContext/BrandConfigContext'
import { runBVQuestionsTrack } from '../../helpers/bazaarVoiceTracking'
import SearchBar from '../SearchBar/SearchBar'

import * as Styled from './QandAComponents.style'

const ModalQuestions = ({ hasError, questionItems }) =>
  questionItems && !hasError ? <div data-test='question-answer-list'>{questionItems}</div> : null

const ErrorMessage = ({ hasError, onClick, searchTerm }) => {
  if (!hasError) return null

  const defaultMessage = 'There was an error getting the questions and answers right now'
  const searchMessage = 'There are no results that match your search. Make sure all words are spelled correctly and '
  const longMessage = searchTerm ? searchMessage : defaultMessage
  const clickMessage = 'try again'

  return (
    <div role='alert'>
      <Alert kind='info' isCloseable={false} className='alert' data-test='q-and-a-modal-alert'>
        {longMessage}
        <Styled.AlertLink onClick={onClick} onKeyPress={onClick} role='link' tabIndex='0'>
          {clickMessage}
        </Styled.AlertLink>
      </Alert>
    </div>
  )
}

// eslint-disable-next-line
const AccordionHeader = ({ questionCount, answerCount, headingText }) => {
  const questionText = questionCount === 1 ? 'question' : 'questions'
  const answerText = answerCount === 1 ? 'answer' : 'answers'
  const summaryText = questionCount ? `${questionCount} ${questionText} | ${answerCount} ${answerText}` : ''

  return (
    <Styled.AccordionHeader data-test='question-answer-accordion-title'>
      {headingText}
      <Styled.AccordionSummaryText data-test='question-answer-summary-text'>{summaryText}</Styled.AccordionSummaryText>
    </Styled.AccordionHeader>
  )
}

const QAndASelectBox = ({ searchTerm, sortOrder, sortOptions, onSortChange }) => {
  const options = Object.keys(sortOptions).map(option => ({ title: sortOptions[option], value: option }))

  return (
    <Styled.SelectBoxWrapper>
      <Select
        disabled={searchTerm}
        onChange={onSortChange}
        value={sortOrder}
        options={options}
        marginBottom={0}
        className='select'
        data-test='qa-select'
        aria-label='sort questions'
      />
    </Styled.SelectBoxWrapper>
  )
}

const AskAQuestion = ({ partNumber, BZAnalyticsData, variant }) => {
  const message = 'Ask a question'
  const url = `/product/${partNumber}/questions`
  const fireBZAnalytics = () =>
    runBVQuestionsTrack('FeaturedEvent', BZAnalyticsData, 'ask', 'pdp_ask_question', 'button')
  return (
    <Styled.AskButtonWrapper>
      <a href={url} data-test='qa-accordion-ask-question-link' onClick={fireBZAnalytics}>
        <Styled.StyledButton variant={variant} kind='secondary'>
          {message}
        </Styled.StyledButton>
      </a>
    </Styled.AskButtonWrapper>
  )
}

const SearchForm = ({ searchTerm, onSearchTermChange, onSubmitSearch }) => (
  <Styled.SearchBarWrapper>
    <SearchBar
      value={searchTerm}
      onChange={onSearchTermChange}
      onSubmit={onSubmitSearch}
      placeholder='Search questions & answers'
      hasIcon
      className='search'
      data-test='q-and-a-searchbar'
    />
  </Styled.SearchBarWrapper>
)

const QandANoQuestion = ({ partNumber, BZAnalyticsData }) => (
  <Styled.NoQuestionWrapper data-test='qa-no-question-wrapper'>
    <Styled.NoQuestionContent data-test='qa-no-question-content'>
      This area looks a bit empty...be the first to ask a question!
    </Styled.NoQuestionContent>
    <AskAQuestion partNumber={partNumber} BZAnalyticsData={BZAnalyticsData} />
  </Styled.NoQuestionWrapper>
)

const CustomerQuestionsHeader = ({
  onSearchTermChange,
  searchTerm,
  sortOrder,
  sortOptions,
  onSortChange,
  onSubmitSearch,
  partNumber,
  BZAnalyticsData
}) => {
  const { ASK_QUESTION } = useBrandConfigContext()
  return (
    <Styled.CustomerQuestionWrapper>
      {ASK_QUESTION && <AskAQuestion partNumber={partNumber} BZAnalyticsData={BZAnalyticsData} />}
      <SearchForm searchTerm={searchTerm} onSearchTermChange={onSearchTermChange} onSubmitSearch={onSubmitSearch} />
      <QAndASelectBox
        searchTerm={searchTerm}
        sortOrder={sortOrder}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
      />
    </Styled.CustomerQuestionWrapper>
  )
}

export { ModalQuestions, AccordionHeader, ErrorMessage, AskAQuestion, SearchForm, QAndASelectBox, QandANoQuestion }
export default CustomerQuestionsHeader
